import { useEffect, useState } from 'react';
import { apiFetch } from '../../utils/api';
import { useMaintenanceNotice } from '@maintenance-notice';
import AdminHeader from './components/AdminHeader';

async function readError(response) {
    const text = await response.text();
    if (!text) return 'Unable to update the release.';
    try {
        const body = JSON.parse(text);
        if (typeof body === 'string') return body;
        return body.message || body.title || text;
    } catch {
        return text;
    }
}

const releaseStatusLabels = {
    scheduled: 'Scheduled',
    updating: 'Updating',
    completed: 'Completed',
    cancelled: 'Cancelled',
    failed: 'Failed'
};

export default function ReleaseSchedule() {
    const { notice, refresh } = useMaintenanceNotice();
    const [message, setMessage] = useState('');
    const [minutes, setMinutes] = useState(10);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [history, setHistory] = useState([]);

    const phase = notice?.phase || 'idle';
    const active = phase === 'scheduled' || phase === 'updating';

    const loadHistory = async () => {
        const response = await apiFetch('/api/maintenance/history');
        if (!response.ok) return;
        setHistory(await response.json());
    };

    useEffect(() => {
        loadHistory();
    }, [notice?.phase, notice?.id]);

    const schedule = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError('');
        const response = await apiFetch('/api/maintenance', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message, minutes: Number(minutes) })
        });
        setSaving(false);
        if (!response.ok) {
            setError(await readError(response));
            return;
        }
        setMessage('');
        await refresh();
    };

    const cancel = async () => {
        setSaving(true);
        setError('');
        const response = await apiFetch('/api/maintenance', { method: 'DELETE' });
        setSaving(false);
        if (!response.ok) {
            setError(await readError(response));
            return;
        }
        await refresh();
    };

    return (
        <div style={{ padding: '1rem 1.25rem', maxWidth: '760px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <AdminHeader
                title="Scheduled release"
                subtitle="Warn signed-in users, then let the host update the site when the timer ends."
                breadcrumb="Release"
            />

            {active && (
                <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <strong>{phase === 'updating' ? 'Update in progress' : 'Countdown running'}</strong>
                    <div>{notice.message}</div>
                    {phase === 'scheduled' && notice.endsAt && (
                        <div style={{ color: 'var(--text-secondary)' }}>
                            Updates at {new Date(notice.endsAt).toLocaleString()}
                        </div>
                    )}
                    <div>
                        <button type="button" className="btn-secondary" disabled={saving} onClick={cancel}>
                            {phase === 'updating' ? 'Clear update notice' : 'Cancel release'}
                        </button>
                    </div>
                </div>
            )}

            <form onSubmit={schedule} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    Message
                    <textarea
                        className="input-field"
                        value={message}
                        maxLength={240}
                        rows={3}
                        required
                        disabled={saving || phase === 'updating'}
                        onChange={event => setMessage(event.target.value)}
                        placeholder="We are releasing a new version."
                    />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', maxWidth: '220px' }}>
                    Timer (minutes)
                    <input
                        className="input-field"
                        type="number"
                        min={1}
                        max={10080}
                        required
                        disabled={saving || phase === 'updating'}
                        value={minutes}
                        onChange={event => setMinutes(event.target.value)}
                    />
                </label>
                <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                    Use at least 10 minutes so the image build can finish before the countdown ends. If the build is still running when the timer hits zero, the update waits until the build finishes.
                </p>
                {error && <div style={{ color: 'var(--error)' }}>{error}</div>}
                <div>
                    <button type="submit" className="btn-primary" disabled={saving || phase === 'updating'}>
                        {phase === 'scheduled' ? 'Replace countdown' : 'Start countdown'}
                    </button>
                </div>
            </form>

            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.05rem' }}>Release log</h2>
                {history.length === 0 ? (
                    <p style={{ margin: 0, color: 'var(--text-secondary)' }}>No releases yet.</p>
                ) : history.map(row => (
                    <div key={row.id} style={{ borderTop: '1px solid var(--border)', paddingTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <strong>{releaseStatusLabels[row.phase] || row.phase}</strong>
                        <div>{row.message}</div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                            Scheduled for {new Date(row.endsAt).toLocaleString()}
                            {row.finishedAt ? ` · Finished ${new Date(row.finishedAt).toLocaleString()}` : ''}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
